"use client"

import { createContext, useContext, type ReactNode } from "react"

/**
 * The per-country nav-hide list, resolved once in `[country]/[locale]/layout.tsx`
 * and read by `PageShell` (and through it, the mega menu + mobile menu).
 *
 * Why a context rather than a prop threaded through the route-group layouts
 * (AHD-9096): so those layouts need no `await` at all. `getServicesConfig()`
 * used to be awaited in each of them, which made it serial — `[locale]/layout`
 * finished its own CMS read first, and only then did the route-group layout
 * start this one. Resolving it in the parent puts both reads in one
 * `Promise.all` and leaves the route-group layouts as two-line synchronous
 * functions.
 *
 * A word of warning attached to the same ticket, because the two are easy to
 * conflate: hoisting this read is NOT what put the site's chrome back into the
 * static shell. That was removing the two `<Suspense fallback={null}>`
 * boundaries in `[locale]/layout.tsx` — with either of them in place, React
 * refuses to wait for anything below and writes the whole visible page into a
 * `<div hidden id="S:0">` staging container instead, whether or not these
 * layouts await. Measured: making both layouts fully synchronous while the
 * boundaries were still there produced a byte-identical shape. Don't reach for
 * this pattern expecting it to fix a blank first paint; read the comment where
 * those boundaries used to be.
 */
const NavHiddenPathsContext = createContext<string[]>([])

export function NavHiddenPathsProvider({
  value,
  children,
}: {
  value: string[]
  children: ReactNode
}) {
  return (
    <NavHiddenPathsContext.Provider value={value}>
      {children}
    </NavHiddenPathsContext.Provider>
  )
}

/**
 * Bare `/tests/<slug>` paths to hide from the core navigation (mega menu +
 * mobile menu) for the active country — the CMS `products.marketStatus[]`
 * `hideFromNav` flag, resolved server-side.
 *
 * Defaults to `[]` (nav ungated), which is the same fail-loud-but-degrade
 * contract `getServicesConfig` itself has: an empty CMS response renders the
 * nav ungated and fires a Sentry error rather than silently hiding services.
 */
export function useNavHiddenPaths(): string[] {
  return useContext(NavHiddenPathsContext)
}
